import { Injectable } from '@angular/core';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {

  isEmpty(value: any): boolean {
    return value === null // NULL value
      || value === undefined // undefined
      || value === 'undefined' // undefined
      || value.length === 0 // Array is empty
      || value === '00000000-0000-0000-0000-000000000000' // Guid empty
      || ((value instanceof Date && !isNaN(value.valueOf())) // Validate DateTime value and check min-max value
        && ((value <= new Date(1753, 1, 1)) // SQL DateTime minimum value
          || (value >= new Date(9999, 12, 31, 23, 59, 59, 999))) // SQL DateTime maximum value
      );
  }

  dateDiff(source: Date, dest: Date): number {
    source.setSeconds(0);
    dest.setSeconds(0);
    const a: any = moment(source);
    const b: any = moment(dest);
    return a.diff(b);
  }

  async getBase64(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  getDeepCopy(value: any): any {
    if (!value) { return; }
    return Object.assign({}, value);
  }

  formatDate(date: string | Date, format?: string): string {
    if (!date) { return ''; }
    if (!this.isEmpty(format)) {
      return moment(date).local().format("DD-MM-YYYY HH:mm");
    }
    else {
      return moment(date).local().format(format);
    }
  }

  getMinutesBetweenTwoDates(startDate: Date, endDate: Date): number {
    return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
  }

  combineDateAndTime(date: Date, time: Date): Date | undefined {
    if (!date) { return; }

    date = new Date(date);
    if (time) {
      time = new Date(time);
      date.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    } else {
      date = this.getDefaultTime(date);
    }
    return date;
  }

  getDefaultTime(date?: Date): Date {
    if (date) {
      date = new Date(date);
    } else {
      date = new Date();
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

  isToday(date: Date): boolean {
    const today: Date = new Date();
    return moment(date).local().format('DD-MM-YYYY') === moment(today).local().format('DD-MM-YYYY');
  }

  getLocalDateString(utcDate: Date, format?: string): string {
    if (!utcDate) { return ''; }
    return this.formatDate(this.getLocalDate(utcDate), format);
  }

  getLocalDate(utcDate: Date): Date {
    return moment.utc(utcDate).local().toDate();
  }

  getUtcDateInSqlFormat(date: Date) {
    const iso: string = 'YYYY-MM-DDTHH:mm:ss';
    return moment.utc(date).format(iso);
  }

  appendToString(appendTo: string, appendWith: string, infix: string): string {
    if (!appendWith) { return appendTo; }
    if (!appendTo) { return appendWith; }

    if (infix) {
      appendTo += infix + appendWith;
    }

    return appendTo;
  }

  isIEOrEdge(): boolean {
    return /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  }

  combineIntegerAndDecimals(integer: string, decimals: string): number {
    if (!integer && !decimals) { return 0; }
    if (!decimals) { return +(integer); }
    if (!integer) { return +('.' + decimals); }
    return +(integer + '.' + decimals);
  }

  getIntegerFromNumber(value: number): string | undefined {
    if (!value) { return; }
    return value.toString().split('.')[0];
  }

  getDecimalsFromNumber(value: number): string | undefined {
    if (!value) { return; }
    return value.toString().split('.')[1];
  }

  getFileName(filepath: string) {
    return filepath.replace(/^.*[\\/]/, '');
  }

  removeElementFromArray(element: any, array: any[]) {
    const index: number = array.indexOf(element);
    array.splice(index, 1);
  }

  windowWidthGreaterThan(width: number): boolean {
    return window.innerWidth >= width;
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  generateUUID(): string {
    return `${this.s4()}${this.s4()}-${this.s4()}-${this.s4()}-${this.s4()}-${this.s4()}${this.s4()}${this.s4()}`;
  }
  
  // Used for UUIDGenerator
  private s4(): string {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
}

